import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { crmAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Users,
  Plus,
  Search,
  Building2,
  Mail,
  Phone,
  MapPin,
  TrendingUp,
  ShoppingCart,
  Euro,
  FileText,
  Package,
  Globe,
  CheckCircle,
  Upload,
  Download,
  Laptop,
  Key,
  Send,
  MessageSquare,
  Clock,
  Bell,
  History,
  Loader2,
  Target,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import ProspectionH24Tab from '@/components/ProspectionH24Tab';

const statutColors = {
  prospect: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  actif: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  inactif: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  perdu: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const typeClientIcons = {
  B2B: Building2,
  B2C: Users,
  cooperative: Package,
  negoce: TrendingUp,
  export: Globe,
};

const ClientCard = ({ client, onClick }) => {
  const Icon = typeClientIcons[client.type_client] || Users;
  const stats = client.stats || {};
  const chaleurMeta = {
    chaud: { label: 'Chaud', cls: 'bg-emerald-100 text-emerald-700' },
    tiede: { label: 'Tiède', cls: 'bg-amber-100 text-amber-700' },
    froid: { label: 'Froid', cls: 'bg-rose-100 text-rose-700' },
    inconnu: { label: '—', cls: 'bg-gray-100 text-gray-500' },
  }[stats.chaleur || 'inconnu'];

  return (
    <Card
      className="card-hover cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => onClick(client)}
      data-testid={`client-card-${client.id}`}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{client.nom}</h3>
              {client.entreprise && (
                <p className="text-xs text-muted-foreground truncate">{client.entreprise}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge className={statutColors[client.statut]}>{client.statut}</Badge>
            <Badge className={chaleurMeta.cls + ' text-[10px]'} title={`Dernier contact : ${stats.dernier_contact ? new Date(stats.dernier_contact).toLocaleDateString('fr-FR') : 'jamais'}`}>
              {stats.chaleur === 'chaud' ? '🔥' : stats.chaleur === 'tiede' ? '☀️' : stats.chaleur === 'froid' ? '❄️' : '·'} {chaleurMeta.label}
            </Badge>
          </div>
        </div>

        {/* Mini-stats agrégées */}
        <div className="grid grid-cols-3 gap-2 my-3 text-[11px]" data-testid={`client-stats-${client.id}`}>
          <div className="bg-emerald-50 rounded p-1.5 text-center">
            <div className="font-bold text-emerald-700">{stats.interventions || 0}</div>
            <div className="text-emerald-600">Interv.</div>
          </div>
          <div className="bg-blue-50 rounded p-1.5 text-center">
            <div className="font-bold text-blue-700">{(stats.parcelles_liees || 0)}</div>
            <div className="text-blue-600">Parcelles</div>
          </div>
          <div className="bg-amber-50 rounded p-1.5 text-center">
            <div className="font-bold text-amber-700">{(stats.surface_geree_ha || stats.parcelles_surface_ha || 0).toFixed(1)}</div>
            <div className="text-amber-600">ha gérés</div>
          </div>
        </div>

        <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2">
          {stats.communications > 0 && (
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{stats.communications}</span>
          )}
          {stats.emails_opens > 0 && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium" title="Emails ouverts par ce client">
              📧 {stats.emails_opens} ouv.
            </span>
          )}
          {stats.actions_ouvertes > 0 && (
            <span className="flex items-center gap-1 text-orange-600 font-medium" title="Actions CRM en cours">
              <Bell className="w-3 h-3" />{stats.actions_ouvertes}
            </span>
          )}
          {stats.devis_envoyes > 0 && (
            <span className="flex items-center gap-1 text-purple-600">
              <FileText className="w-3 h-3" />{stats.devis_envoyes} devis
            </span>
          )}
        </div>

        <div className="space-y-1 text-xs mt-2 text-muted-foreground">
          {client.email && (
            <div className="flex items-center gap-2 truncate">
              <Mail className="w-3 h-3" />
              <span className="truncate">{client.email}</span>
            </div>
          )}
          {client.ville && (
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3" />
              <span>{client.ville}{client.pays ? `, ${client.pays}` : ''}</span>
            </div>
          )}
        </div>

        <div className="mt-3 pt-2 border-t border-border flex items-center justify-between">
          <Badge variant="outline" className="text-[10px]">{client.type_client}</Badge>
          <div className="text-right">
            <div className="font-bold text-primary text-sm">
              {((stats.ca_cumule_ht || client.valeur_totale || 0)).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€
            </div>
            <div className="text-[10px] text-muted-foreground">CA cumulé HT</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CommandeCard = ({ commande }) => {
  const statutColors = {
    devis: 'bg-gray-100 text-gray-800',
    confirmee: 'bg-blue-100 text-blue-800',
    en_preparation: 'bg-amber-100 text-amber-800',
    livree: 'bg-green-100 text-green-800',
    facturee: 'bg-purple-100 text-purple-800',
    annulee: 'bg-red-100 text-red-800',
  };

  return (
    <Card data-testid={`commande-card-${commande.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold">{commande.numero}</p>
            <p className="text-sm text-muted-foreground">{commande.date_commande}</p>
          </div>
          <Badge className={statutColors[commande.statut]}>
            {commande.statut.replace('_', ' ')}
          </Badge>
        </div>
        
        <div className="space-y-1 text-sm">
          {commande.lignes.slice(0, 2).map((ligne, index) => (
            <div key={index} className="flex justify-between">
              <span className="text-muted-foreground">{ligne.produit}</span>
              <span>{ligne.quantite} × {ligne.prix_unitaire}€</span>
            </div>
          ))}
          {commande.lignes.length > 2 && (
            <p className="text-muted-foreground">+{commande.lignes.length - 2} autres produits</p>
          )}
        </div>
        
        <div className="mt-4 pt-3 border-t border-border flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Total TTC</span>
          <span className="text-lg font-bold">{commande.total_ttc.toLocaleString()}€</span>
        </div>
      </CardContent>
    </Card>
  );
};

const ClientDetail = ({ client }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          {React.createElement(typeClientIcons[client.type_client] || Users, {
            className: "w-8 h-8 text-primary"
          })}
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold">{client.nom}</h2>
          {client.entreprise && (
            <p className="text-muted-foreground">{client.entreprise}</p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge className={statutColors[client.statut]}>{client.statut}</Badge>
            <Badge variant="outline">{client.type_client}</Badge>
          </div>
        </div>
        <Button onClick={() => navigate(`/clients/${client.id}`)} data-testid="btn-fiche-360">
          🎯 Fiche 360°
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-2 gap-4">
          {client.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{client.email}</span>
            </div>
          )}
          {client.telephone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span>{client.telephone}</span>
            </div>
          )}
          {client.adresse && (
            <div className="flex items-center gap-2 col-span-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span>{client.adresse}, {client.ville} {client.pays}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-muted-foreground">Valeur totale</p>
            <p className="text-3xl font-bold text-primary">{client.valeur_totale.toLocaleString()}€</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-muted-foreground">Dernier contact</p>
            <p className="text-lg font-semibold">{client.dernier_contact || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {client.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{client.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Need to import React for createElement
import React from 'react';

// Composant Prospects Logiciel - CRM unifié
const ProspectsLogicielTab = () => {
  const [prospects, setProspects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedProspect, setSelectedProspect] = React.useState(null);
  const [generatingAccess, setGeneratingAccess] = React.useState(false);
  const [showCredentials, setShowCredentials] = React.useState(null);

  React.useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const response = await api.get('/crm-vente-logiciel/prospects');
      setProspects(response.data?.prospects || []);
    } catch (error) {
      console.error('Erreur chargement prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const genererAcces = async (prospectId) => {
    setGeneratingAccess(true);
    try {
      const response = await api.post(`/crm-vente-logiciel/prospects/${prospectId}/generer-acces`);
      if (response.data?.success) {
        setShowCredentials(response.data.credentials);
        toast.success('Codes d\'accès générés !');
        fetchProspects();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur génération accès');
    } finally {
      setGeneratingAccess(false);
    }
  };

  const statutColors = {
    nouveau: 'bg-blue-100 text-blue-800',
    prix_valide: 'bg-amber-100 text-amber-800',
    gagne: 'bg-green-100 text-green-800',
    perdu: 'bg-red-100 text-red-800'
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats rapides */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total prospects</span>
            </div>
            <p className="text-2xl font-bold">{prospects.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">En attente</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">
              {prospects.filter(p => p.statut === 'nouveau' || p.statut === 'prix_valide').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Convertis</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {prospects.filter(p => p.statut === 'gagne').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Euro className="w-4 h-4" />
              <span className="text-sm">MRR potentiel</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {prospects.reduce((acc, p) => acc + (p.prix_mensuel_valide || p.prix_mensuel_propose || 0), 0).toLocaleString()}€
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des prospects */}
      <div className="space-y-3">
        {prospects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Laptop className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Aucun prospect logiciel</p>
              <p className="text-muted-foreground">Utilisez le configurateur pour créer des prospects</p>
            </CardContent>
          </Card>
        ) : (
          prospects.map(prospect => (
            <Card key={prospect.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{prospect.nom_entreprise}</h3>
                      <Badge className={statutColors[prospect.statut] || 'bg-gray-100'}>
                        {prospect.statut === 'gagne' ? 'Client actif' : 
                         prospect.statut === 'prix_valide' ? 'Prix validé' :
                         prospect.statut === 'nouveau' ? 'Nouveau' : prospect.statut}
                      </Badge>
                      {prospect.acces_genere && (
                        <Badge variant="outline" className="border-green-500 text-green-600">
                          <Key className="w-3 h-3 mr-1" />
                          Accès créé
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-5 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-muted-foreground">Contact</p>
                        <p>{prospect.contact_prenom} {prospect.contact_nom}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="truncate">{prospect.contact_email}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Surface</p>
                        <p>{prospect.surface_ha} ha</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Prix/mois</p>
                        <p className="font-semibold text-green-600">
                          {prospect.prix_mensuel_valide || prospect.prix_mensuel_propose}€
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Commercial</p>
                        <p>{prospect.commercial_nom || '-'}</p>
                      </div>
                    </div>

                    {/* Activités/Packs */}
                    {prospect.activites?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {prospect.activites.map(a => (
                          <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                        ))}
                      </div>
                    )}

                    {/* Historique récent */}
                    {prospect.historique?.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <History className="w-3 h-3" />
                        <span>
                          Dernière action: {prospect.historique[prospect.historique.length - 1]?.action} 
                          ({new Date(prospect.historique[prospect.historique.length - 1]?.date).toLocaleDateString('fr-FR')})
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {!prospect.acces_genere && (prospect.statut === 'prix_valide' || prospect.statut === 'gagne') && (
                      <Button 
                        size="sm" 
                        onClick={() => genererAcces(prospect.id)}
                        disabled={generatingAccess}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {generatingAccess ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Key className="w-4 h-4 mr-1" />
                            Générer accès
                          </>
                        )}
                      </Button>
                    )}
                    {prospect.acces_genere && prospect.credentials_client && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setShowCredentials(prospect.credentials_client)}
                      >
                        <Key className="w-4 h-4 mr-1" />
                        Voir accès
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setSelectedProspect(prospect)}>
                      <FileText className="w-4 h-4 mr-1" />
                      Détails
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog Credentials */}
      <Dialog open={!!showCredentials} onOpenChange={() => setShowCredentials(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-600" />
              Codes d'accès client
            </DialogTitle>
          </DialogHeader>
          {showCredentials && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 font-medium mb-3">
                  Transmettez ces identifiants au client :
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">URL de connexion</Label>
                    <p className="font-mono bg-white p-2 rounded border">{showCredentials.url_connexion}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="font-mono bg-white p-2 rounded border">{showCredentials.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Mot de passe</Label>
                    <p className="font-mono bg-white p-2 rounded border font-bold">{showCredentials.mot_de_passe}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  className="flex-1" 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `URL: ${showCredentials.url_connexion}\nEmail: ${showCredentials.email}\nMot de passe: ${showCredentials.mot_de_passe}`
                    );
                    toast.success('Copié dans le presse-papier');
                  }}
                >
                  Copier tout
                </Button>
                <Button className="flex-1">
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer par email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Détail Prospect */}
      <Dialog open={!!selectedProspect} onOpenChange={() => setSelectedProspect(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détail du prospect</DialogTitle>
          </DialogHeader>
          {selectedProspect && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Entreprise</Label>
                  <p className="font-semibold">{selectedProspect.nom_entreprise}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Statut</Label>
                  <Badge className={statutColors[selectedProspect.statut]}>{selectedProspect.statut}</Badge>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Contact</Label>
                  <p>{selectedProspect.contact_prenom} {selectedProspect.contact_nom}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p>{selectedProspect.contact_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Téléphone</Label>
                  <p>{selectedProspect.contact_telephone || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Ville</Label>
                  <p>{selectedProspect.ville || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">Configuration</Label>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">Surface</p>
                    <p className="font-bold">{selectedProspect.surface_ha} ha</p>
                  </div>
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">Workspaces</p>
                    <p className="font-bold">{selectedProspect.nb_workspaces}</p>
                  </div>
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">Collaborateurs</p>
                    <p className="font-bold">{selectedProspect.nb_collaborateurs}</p>
                  </div>
                </div>
              </div>

              {/* Historique complet */}
              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground mb-2 block">Historique CRM</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedProspect.historique?.map((h, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-muted/50 rounded">
                      <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">{h.action}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(h.date).toLocaleString('fr-FR')} - {h.details}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const CRMPage = () => {
  const [clients, setClients] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeTab, setActiveTab] = useState('clients');
  const [filterStatut, setFilterStatut] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsEnrichisRes, commandesRes, statsRes] = await Promise.all([
          api.get('/crm-enrichi/clients-enrichis').catch(() => null),
          crmAPI.getCommandes(),
          crmAPI.getStats(),
        ]);
        // Si l'enrichi répond, on utilise (avec stats agrégées). Sinon fallback sur l'ancien.
        if (clientsEnrichisRes?.data?.clients?.length > 0) {
          setClients(clientsEnrichisRes.data.clients);
        } else {
          const fallback = await crmAPI.getClients();
          setClients(fallback.data || []);
        }
        setCommandes(commandesRes.data);
        setStats(statsRes.data);
      } catch (error) {
        toast.error('Erreur lors du chargement du CRM');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredClients = clients.filter((c) => {
    const matchesSearch = c.nom.toLowerCase().includes(search.toLowerCase()) ||
      (c.entreprise?.toLowerCase().includes(search.toLowerCase())) ||
      (c.email?.toLowerCase().includes(search.toLowerCase()));
    const matchesStatut = filterStatut === 'all' || c.statut === filterStatut;
    return matchesSearch && matchesStatut;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CRM / Commerce</h1>
          <p className="text-muted-foreground mt-1">Gérez vos clients et commandes</p>
        </div>
        <Button className="rounded-full gap-2" data-testid="add-client-button">
          <Plus className="w-4 h-4" />
          Nouveau client
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Clients actifs</span>
              </div>
              <p className="text-2xl font-bold">{stats.clients_actifs}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Prospects</span>
              </div>
              <p className="text-2xl font-bold">{stats.prospects}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <ShoppingCart className="w-4 h-4" />
                <span className="text-sm">Commandes en cours</span>
              </div>
              <p className="text-2xl font-bold">{stats.commandes_en_cours}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Euro className="w-4 h-4" />
                <span className="text-sm">CA Total</span>
              </div>
              <p className="text-2xl font-bold">{(stats.ca_total / 1000).toFixed(0)}k€</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="clients" data-testid="tab-clients">
            <Users className="w-4 h-4 mr-2" />
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="commandes" data-testid="tab-commandes">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Commandes ({commandes.length})
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">
            <TrendingUp className="w-4 h-4 mr-2" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="prospects-logiciel" data-testid="tab-prospects-logiciel">
            <Laptop className="w-4 h-4 mr-2" />
            Prospects Logiciel
          </TabsTrigger>
          <TabsTrigger value="prospection-h24" data-testid="tab-prospection-h24">
            <Target className="w-4 h-4 mr-2" />
            Prospection IA
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="client-search-input"
              />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-40" data-testid="filter-statut-select">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="actif">Actifs</SelectItem>
                <SelectItem value="prospect">Prospects</SelectItem>
                <SelectItem value="inactif">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clients grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onClick={setSelectedClient}
              />
            ))}
          </div>

          {filteredClients.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-lg font-medium">Aucun client trouvé</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="commandes" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commandes.map((commande) => (
              <CommandeCard key={commande.id} commande={commande} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-4">
          {/* Pipeline Kanban style HubSpot */}
          <div className="flex gap-4 overflow-x-auto pb-4">
            {/* Colonne Prospects */}
            <div className="flex-shrink-0 w-72 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <h3 className="font-semibold text-sm">Prospects</h3>
                </div>
                <Badge variant="secondary">{clients.filter(c => c.statut === 'prospect').length}</Badge>
              </div>
              <div className="space-y-2">
                {clients.filter(c => c.statut === 'prospect').map(client => (
                  <Card key={client.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedClient(client)}>
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{client.nom}</p>
                      {client.entreprise && <p className="text-xs text-muted-foreground">{client.entreprise}</p>}
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="outline" className="text-xs">{client.type_client}</Badge>
                        <span className="text-xs font-semibold text-primary">{client.valeur_totale?.toLocaleString() || 0}€</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Colonne En négociation */}
            <div className="flex-shrink-0 w-72 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <h3 className="font-semibold text-sm">En négociation</h3>
                </div>
                <Badge variant="secondary">{commandes.filter(c => c.statut === 'devis').length}</Badge>
              </div>
              <div className="space-y-2">
                {commandes.filter(c => c.statut === 'devis').map(commande => (
                  <Card key={commande.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{commande.numero}</p>
                      <p className="text-xs text-muted-foreground">{commande.client_nom || 'Client'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{commande.date_commande}</span>
                        <span className="text-sm font-bold text-amber-600">{commande.total_ttc?.toLocaleString() || 0}€</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Colonne Confirmées */}
            <div className="flex-shrink-0 w-72 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <h3 className="font-semibold text-sm">Confirmées</h3>
                </div>
                <Badge variant="secondary">{commandes.filter(c => c.statut === 'confirmee').length}</Badge>
              </div>
              <div className="space-y-2">
                {commandes.filter(c => c.statut === 'confirmee').map(commande => (
                  <Card key={commande.id} className="cursor-pointer hover:shadow-md transition-shadow border-green-200">
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{commande.numero}</p>
                      <p className="text-xs text-muted-foreground">{commande.client_nom || 'Client'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{commande.date_commande}</span>
                        <span className="text-sm font-bold text-green-600">{commande.total_ttc?.toLocaleString() || 0}€</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Colonne Livrées */}
            <div className="flex-shrink-0 w-72 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <h3 className="font-semibold text-sm">Livrées</h3>
                </div>
                <Badge variant="secondary">{commandes.filter(c => c.statut === 'livree').length}</Badge>
              </div>
              <div className="space-y-2">
                {commandes.filter(c => c.statut === 'livree').map(commande => (
                  <Card key={commande.id} className="cursor-pointer hover:shadow-md transition-shadow border-purple-200">
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{commande.numero}</p>
                      <p className="text-xs text-muted-foreground">{commande.client_nom || 'Client'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{commande.date_commande}</span>
                        <span className="text-sm font-bold text-purple-600">{commande.total_ttc?.toLocaleString() || 0}€</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Colonne Facturées */}
            <div className="flex-shrink-0 w-72 bg-muted/30 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <h3 className="font-semibold text-sm">Facturées</h3>
                </div>
                <Badge variant="secondary">{commandes.filter(c => c.statut === 'facturee').length}</Badge>
              </div>
              <div className="space-y-2">
                {commandes.filter(c => c.statut === 'facturee').map(commande => (
                  <Card key={commande.id} className="cursor-pointer hover:shadow-md transition-shadow border-emerald-200">
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{commande.numero}</p>
                      <p className="text-xs text-muted-foreground">{commande.client_nom || 'Client'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-bold text-emerald-600">{commande.total_ttc?.toLocaleString() || 0}€</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Résumé du pipeline */}
          {stats?.pipeline && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <Card className="bg-blue-50 dark:bg-blue-950/20">
                <CardContent className="pt-4">
                  <p className="text-xs text-blue-600 font-medium">Total Prospects</p>
                  <p className="text-2xl font-bold text-blue-700">{clients.filter(c => c.statut === 'prospect').reduce((acc, c) => acc + (c.valeur_totale || 0), 0).toLocaleString()}€</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 dark:bg-amber-950/20">
                <CardContent className="pt-4">
                  <p className="text-xs text-amber-600 font-medium">Devis en attente</p>
                  <p className="text-2xl font-bold text-amber-700">{stats.pipeline.devis?.toLocaleString() || 0}€</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 dark:bg-green-950/20">
                <CardContent className="pt-4">
                  <p className="text-xs text-green-600 font-medium">Confirmées</p>
                  <p className="text-2xl font-bold text-green-700">{stats.pipeline.confirmee?.toLocaleString() || 0}€</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 dark:bg-emerald-950/20">
                <CardContent className="pt-4">
                  <p className="text-xs text-emerald-600 font-medium">Facturées</p>
                  <p className="text-2xl font-bold text-emerald-700">{stats.pipeline.facturee?.toLocaleString() || 0}€</p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Tab Prospects Logiciel */}
        <TabsContent value="prospects-logiciel" className="space-y-4">
          <ProspectsLogicielTab />
        </TabsContent>

        {/* Tab Prospection H24 IA */}
        <TabsContent value="prospection-h24" className="space-y-4">
          <ProspectionH24Tab />
        </TabsContent>
      </Tabs>

      {/* Client detail dialog */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détail du client</DialogTitle>
          </DialogHeader>
          {selectedClient && <ClientDetail client={selectedClient} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMPage;
